import React, { useEffect, useMemo } from 'react';
import { Box, Heading, Sidebar, Text } from '@common-fe/common-fe';

import { OrganizationTypes } from '@/common/constants';
import Permissions from '@/common/permissions';
// import { CardTypesEnum } from '@common-fe/common-fe';
import { useSidebarStore } from '@/modules/core/components/Sidebar/store/useSidebarStore';
import { useCurrentOrganization, useHasAccess } from '@/modules/core/hooks';
import {
  TOC_CONTAINER_UI_ID, TOC_MODULE_UI_ID,
} from '@/modules/core/hooks/useDetectActiveTOCModule';
import { HEALTH_PLAN_MODULES } from '@/modules/HealthPlan/HealthPlan.constants';
import { useOnlyForDev } from '@/utils';

// import PlanTypeIcon from './PlanTypeIcon';
import { useHealthPlanNameStore } from '../BasicProperties/HealthPlanName/stores';
import { useHealthPlanSetupHiddenModules } from '../hooks';

import { useHealthPlanModules,useHealthPlanProgress, useProgressScrollState } from './hooks';
import {
  ItemWrapper,
  ListContainer,
  StyledButton,
  StyledText,
} from './Sidebar.styles';

interface Props {
  scrollToSection?: (data: { module?: string, section?: string }) => void;
  isTemplateModeApplied?: boolean;
  isHealthPlanActive?: boolean
}

const ProgressSidebar: React.FC<Props> = ({ scrollToSection, isTemplateModeApplied, isHealthPlanActive}) => {
  const { total, percent, passed } = useHealthPlanProgress();
  const isOnlyForDev = useOnlyForDev();
  const hiddenModules = useHealthPlanSetupHiddenModules();
  const isHasAccess = useHasAccess([
    {
      permission: Permissions.EDIT_DRAFT_PLAN,
    },
    {

      permission: Permissions.ACTIVATE_PLAN,
    },
  ]);
  const planName = useHealthPlanNameStore((state) => state.state.healthPlanName);
  const { observingOrganization: { type } } = useCurrentOrganization();
  const isSubsidiary = useMemo(
    () => type === OrganizationTypes.subsidiary,
    [type],
  );

  const config = useHealthPlanModules(!isHasAccess || isSubsidiary || (!isOnlyForDev && isHealthPlanActive && !isTemplateModeApplied)
    ? [HEALTH_PLAN_MODULES.FINAL_SETUP, ...hiddenModules] : hiddenModules);

  const { expandSidebar } = useSidebarStore();
  const { activeModule } = useProgressScrollState();

  useEffect(() => {
    expandSidebar(false);
    return () => {
      expandSidebar(true);
    };
  }, [expandSidebar]);

  return (
    <Sidebar
      width={{
        width: 'xmedium',
        min: 'xmedium',
      }}
      pad={{
        top: 'spacing24',
        left: 'spacing12',
      }}
      height="100vh"
      background="module"
      gap=""
      header={(
        <Heading level={4} size="medium" margin="none" color="textTitle">
          {planName}
        </Heading>
      )}
    >
      <Box margin={{ top: 'spacing12', bottom: 'spacing16' }}>
        <Text
          data-testid="plan-configuration-progress"
          weight={500}
          size="small"
          color={percent === 100 ? 'textAccent' : 'textSecondary'}
        >
          {passed}/{total} ({percent}%)
        </Text>
      </Box>
      <Box
        background="border2"
        pad="spacing12"
        margin={{ right: 'spacing12' }}
        round="checkboxRound"
      >
        <ListContainer>
          <Box id={TOC_CONTAINER_UI_ID}>
            {Object.keys(config).map((anchorId) => (
              <ItemWrapper
                key={anchorId}
                id={TOC_MODULE_UI_ID(anchorId)}
                data-testid={`select-plan-module-${anchorId}`}
                style={{
                  boxShadow: 'none',
                }}
                // align="center"
                justify="center"
                pad={{ bottom: 'spacing8' }}
                onClick={() => (scrollToSection ? scrollToSection({ module: anchorId }) : null)}
              >
                <StyledButton
                  active={activeModule === anchorId}
                >
                  <StyledText
                    size="medium"
                    weight={activeModule === anchorId ? 'bold' : 'normal'}
                  >
                    {config[anchorId].title}
                  </StyledText>
                </StyledButton>
              </ItemWrapper>
            ))}
          </Box>
        </ListContainer>
      </Box>
    </Sidebar>
  );
};

export default ProgressSidebar;
